// src/component/Group2026.js
//
// 2026 グループ活動 ユーザー向け画面（/groups2026/:id）
//
// タブ：仲間 / ガイド / チャット
//   - 仲間：denormalize された members + 各メンバーの DDP（users/{uid}/ddps から最初の 1 件）
//   - ガイド：guides2026/{id} を日付順に表示。本文は HTML
//   - チャット：placeholder（既存 GroupChatEx を接続予定）

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  limit,
  getDocs,
  onSnapshot,
  orderBy,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../api/firebase";

import {
  Box,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  Avatar,
  Grid,
  Button,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Collapse,
  Badge,
} from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";

import { AppThemeContext, AppUserContext } from "./AppShell";
import ChatTab2026 from "./ChatTab2026";

const MINCHO_FONT =
  '"Hiragino Mincho ProN", "Yu Mincho", "YuMincho", "Noto Serif JP", serif';

function formatDateLabel(s) {
  if (!s) return "";
  const [y, m, d] = (s + "").split("-");
  if (!y) return s;
  return `${y}/${m}/${d}`;
}

function formatGuideRange(g) {
  const start = g.dateStart || g.date || "";
  const end = g.dateEnd || g.date || "";
  if (!start && !end) return "";
  const a = formatDateLabel(start);
  const b = formatDateLabel(end);
  if (a && b && a === b) return a;
  if (a && b) return `${a} 〜 ${b}`;
  return a || b;
}

/* ──────────────────────────────────────────────────────────
   仲間タブ — メンバー一覧 + 各人の DDP
   ──────────────────────────────────────────────────────── */
function MembersTab({
  members,
  ddpByUid,
  ddpLoading,
  accent,
  textBase,
  border,
  surface,
  groupId,
  currentUid,
  currentUserName,
  reportedMap = {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null); // { uid, name }
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [isReportConfirmed, setIsReportConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [expandedUids, setExpandedUids] = useState(() => new Set());
  const [reportEnabled, setReportEnabled] = useState(false);

  useEffect(() => {
    const ref = doc(db, "systemConfig", "groupReports2026Config");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setReportEnabled(snap.exists() ? snap.data()?.reportEnabled === true : false);
      },
      (err) => {
        console.error("groupReports2026Config subscribe error:", err);
        setReportEnabled(false);
      }
    );
    return () => unsub();
  }, []);

  const toggleExpanded = (uid) => {
    setExpandedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const resetAfterSubmit = () => {
    setAnchorEl(null);
    setMenuTarget(null);
  };

  // メニュー対象の既存通報は、親から購読している reportedMap から即時に導出する
  // （その場 getDocs だと伝播ラグ・競合ですり抜けるため、リアルタイム購読に統一）
  const menuExisting = menuTarget ? reportedMap[menuTarget.uid] : null;

  const handleMenuClick = (event, uid, name) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuTarget({ uid, name });
  };

  const submitReport = async () => {
    if (!menuTarget || !currentUid || submitting) return;

    // 二重ガード：購読中の map に既にあれば送信しない（誰かが既に通報済み）
    if (reportedMap[menuTarget.uid]) {
      setOpenReportDialog(false);
      setIsReportConfirmed(false);
      resetAfterSubmit();
      return;
    }

    setSubmitting(true);
    try {
      // ドキュメントIDを groupId__reportedUserId に固定し、同一対象は物理的に1件だけにする。
      // これにより「同じ人が複数回」「別の人が同じ対象を」いずれの重複も構造的に発生しない。
      const reportId = `${groupId}__${menuTarget.uid}`;
      await setDoc(
        doc(db, "groupReports2026", reportId),
        {
          reportedUserId: menuTarget.uid,
          reportedUserName: menuTarget.name || "",
          reportedBy: currentUid,
          reportedByName: currentUserName || "",
          reportedAt: serverTimestamp(),
          groupId: groupId || "unknown",
          reportReason: "グループ活動未参加",
        },
        { merge: true }
      );
      setOpenSnackbar(true);
    } catch (err) {
      console.error("通報保存エラー:", err);
    } finally {
      setSubmitting(false);
      setOpenReportDialog(false);
      setIsReportConfirmed(false);
      resetAfterSubmit();
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <PeopleRoundedIcon sx={{ color: accent, fontSize: 18 }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, letterSpacing: "0.08em", color: textBase }}
        >
          仲間（{members.length}名）
        </Typography>
        {ddpLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
      </Stack>

      {members.length === 0 ? (
        <Typography variant="body2" sx={{ color: textBase, opacity: 0.7 }}>
          まだメンバーが登録されていません。
        </Typography>
      ) : (
        <Grid container spacing={1.5}>
          {members.map((m, idx) => {
            const ddpText = ddpByUid[m.uid];
            const locale = m.prefecture || m.usState || m.country || "";
            const isSelf = currentUid && m.uid === currentUid;
            const alreadyReported = !!reportedMap[m.uid];
            return (
              <Grid item xs={12} sm={6} md={6} key={m.uid || idx}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                    height: "100%",
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar
                      src={m.avatar || undefined}
                      alt={m.name || ""}
                      sx={{ width: 40, height: 40, bgcolor: `${accent}20` }}
                    >
                      {(m.name || "?").slice(0, 1)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: textBase,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.name || "(名前未設定)"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: textBase,
                          opacity: 0.65,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {[locale, m.city].filter(Boolean).join(" ・ ")}
                      </Typography>
                    </Box>
                    {!isSelf && reportEnabled && (
                      alreadyReported ? (
                        <Chip
                          size="small"
                          label="報告済み"
                          sx={{
                            bgcolor: "rgba(76,175,80,0.12)",
                            color: "#2e7d32",
                            fontWeight: 700,
                            fontSize: 11,
                            height: 22,
                          }}
                        />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, m.uid, m.name)}
                          sx={{ color: textBase, opacity: 0.6 }}
                          aria-label="メンバーに関する報告"
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )
                    )}
                  </Stack>

                  {/* DDP（アコーディオン） */}
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1,
                      borderTop: `1px dashed ${border}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      onClick={() => toggleExpanded(m.uid)}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: accent,
                          fontWeight: 800,
                          letterSpacing: "0.14em",
                        }}
                      >
                        DDP
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <ExpandMoreRoundedIcon
                        sx={{
                          fontSize: 18,
                          color: accent,
                          transition: "transform 0.2s",
                          transform: expandedUids.has(m.uid)
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    </Stack>
                    <Collapse
                      in={expandedUids.has(m.uid)}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ mt: 0.5 }}>
                        {ddpText === undefined ? (
                          <Typography
                            variant="caption"
                            sx={{ color: textBase, opacity: 0.45 }}
                          >
                            読み込み中…
                          </Typography>
                        ) : ddpText ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: textBase,
                              opacity: 0.92,
                              lineHeight: 1.7,
                              whiteSpace: "pre-line",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {ddpText}
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: textBase, opacity: 0.5 }}
                          >
                            （未登録）
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { mt: 0.5, borderRadius: 2, boxShadow: 3, minWidth: 280, maxWidth: 320 },
        }}
      >
        <MenuItem
          disabled={!!menuExisting}
          onClick={() => {
            setOpenReportDialog(true);
            setAnchorEl(null);
          }}
          sx={{ alignItems: "flex-start", py: 1.5, whiteSpace: "normal" }}
        >
          <Box display="flex" alignItems="flex-start" gap={1.5}>
            <InfoOutlinedIcon sx={{ mt: 0.5, color: accent }} fontSize="small" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                グループ活動未参加を連絡
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", whiteSpace: "normal", wordBreak: "break-word" }}
              >
                事務局に{menuTarget?.name || "このメンバー"}さんの参加状況を報告します
              </Typography>

              {menuExisting && (
                <>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: "#4caf50", display: "block", mt: 0.75 }}
                  >
                    ✅{" "}
                    {menuExisting.reportedAt?.toDate
                      ? new Date(menuExisting.reportedAt.toDate()).toLocaleDateString("ja-JP")
                      : ""}{" "}
                    {menuExisting.reportedByName || "不明"} が報告済み
                  </Typography>

                  {Array.isArray(menuExisting.history) &&
                    menuExisting.history
                      .slice()
                      .sort(
                        (a, b) =>
                          (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
                      )
                      .map((h, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          sx={{
                            display: "block",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            color: "#555",
                            ml: 1,
                            mt: 0.25,
                          }}
                        >
                          📝 {h.text}（事務局 -{" "}
                          {h.createdAt?.toDate
                            ? h.createdAt.toDate().toLocaleDateString("ja-JP")
                            : "不明"}
                          ）
                        </Typography>
                      ))}
                </>
              )}
            </Box>
          </Box>
        </MenuItem>
      </Menu>

      <Dialog
        open={openReportDialog}
        onClose={() => {
          if (submitting) return;
          setOpenReportDialog(false);
          setIsReportConfirmed(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>報告の確認</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            本当に <strong>{menuTarget?.name}</strong> さんの未参加を報告してもよろしいですか？
          </Typography>
          <Box display="flex" alignItems="center">
            <input
              type="checkbox"
              id="confirmReport2026"
              checked={isReportConfirmed}
              onChange={(e) => setIsReportConfirmed(e.target.checked)}
            />
            <label htmlFor="confirmReport2026" style={{ marginLeft: 8 }}>
              この操作は取り消せません。よろしいですか？
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenReportDialog(false);
              setIsReportConfirmed(false);
            }}
            disabled={submitting}
          >
            キャンセル
          </Button>
          <Button
            onClick={submitReport}
            disabled={!isReportConfirmed || submitting || !!menuExisting}
            color="error"
            variant="contained"
          >
            報告する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          報告が完了しました。
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ──────────────────────────────────────────────────────────
   ガイドタブ — 節気 + 日付 + タイトル + HTML 本文
   ──────────────────────────────────────────────────────── */
// 「YYYY-MM-DD」文字列を Date オブジェクトへ
function parseYMD(s) {
  if (!s) return null;
  const [y, m, d] = (s + "").split("-").map(Number);
  if (!y) return null;
  return new Date(y, (m || 1) - 1, d || 1);
}

// today（00:00:00 ローカル）と date の差を日数で返す（正＝未来、0＝今日、負＝過去）
function daysFromToday(date) {
  if (!date) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const ms = date - today;
  return Math.round(ms / (24 * 60 * 60 * 1000));
}

function classifyEach(guides) {
  // 各 guide に status (past|current|future) を付与し、dateStart 昇順で返す
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const enriched = guides.map((g) => {
    const ds = parseYMD(g.dateStart || g.date);
    const de = parseYMD(g.dateEnd || g.date) || ds;
    let status = "future";
    if (ds && de && ds <= today && today <= de) status = "current";
    else if (de && de < today) status = "past";
    return { guide: g, ds, de, status };
  });
  // 開始日 DESC（新しい順）。dateStart が無いものは末尾。
  enriched.sort((a, b) => {
    if (!a.ds && !b.ds) return 0;
    if (!a.ds) return 1;
    if (!b.ds) return -1;
    return b.ds - a.ds;
  });
  return enriched;
}

/* ──────────────────────────────────────────────────────────
   Step 風レイアウト
   左に丸（状態別）と縦線、右にカード本体。
   - past: 小さい塗り丸（薄）
   - current: 大きい塗り丸 + パルス
   - future: 外枠だけの丸
   ──────────────────────────────────────────────────────── */
function StepDot({ status, accent }) {
  if (status === "current") {
    return (
      <Box
        sx={{
          position: "relative",
          width: 18,
          height: 18,
          flexShrink: 0,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: -6,
            borderRadius: "50%",
            border: `2px solid ${accent}`,
            opacity: 0.4,
            animation: "stepPulse 1.8s ease-in-out infinite",
          },
          "@keyframes stepPulse": {
            "0%, 100%": { transform: "scale(0.85)", opacity: 0.4 },
            "50%": { transform: "scale(1.15)", opacity: 0.1 },
          },
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            bgcolor: accent,
            boxShadow: `0 0 0 4px ${accent}22`,
          }}
        />
      </Box>
    );
  }
  if (status === "past") {
    return (
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: accent,
          opacity: 0.35,
          flexShrink: 0,
          mx: "3px",
        }}
      />
    );
  }
  // future
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        bgcolor: "transparent",
        border: `2px dashed ${accent}66`,
        flexShrink: 0,
        mx: "2px",
      }}
    />
  );
}

function StepLine({ accent, status }) {
  // past の縦線は実線で塗り、future は薄い破線で「これから」感
  const isPast = status === "past" || status === "current";
  return (
    <Box
      sx={{
        flexGrow: 1,
        width: 2,
        my: 0.5,
        ...(isPast
          ? { bgcolor: `${accent}55` }
          : {
              backgroundImage: `repeating-linear-gradient(to bottom, ${accent}33 0 4px, transparent 4px 10px)`,
            }),
      }}
    />
  );
}

function GuideStepRow({
  entry,
  isLast,
  accent,
  textBase,
  border,
  surface,
}) {
  const { guide, ds, status } = entry;
  const daysUntil = status === "future" && ds ? daysFromToday(ds) : null;
  const isCurrent = status === "current";
  const isPast = status === "past";
  const [expanded, setExpanded] = useState(false);
  const hasBody = !!(guide.body && guide.body.trim());

  return (
    <Stack direction="row" spacing={2} sx={{ position: "relative" }}>
      {/* 左：dot + line */}
      <Stack alignItems="center" sx={{ minWidth: 22, pt: isCurrent ? 1.5 : 1.25 }}>
        <StepDot status={status} accent={accent} />
        {!isLast && <StepLine status={status} accent={accent} />}
      </Stack>

      {/* 右：カード本体 */}
      <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 3 }}>
        {isCurrent ? (
          <Box
            sx={{
              position: "relative",
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${border}`,
              background: `linear-gradient(160deg, ${accent}14 0%, ${accent}05 60%, rgba(255,255,255,0) 100%)`,
              p: { xs: 2.5, sm: 3.5 },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1.5 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                label="今ここ"
                sx={{
                  bgcolor: accent,
                  color: "#fff",
                  fontWeight: 800,
                  height: 22,
                  letterSpacing: "0.04em",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: textBase,
                  opacity: 0.6,
                  fontWeight: 700,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {formatGuideRange(guide)}
              </Typography>
            </Stack>
            {guide.sekki && (
              <Typography
                sx={{
                  fontFamily: MINCHO_FONT,
                  fontWeight: 500,
                  fontSize: { xs: 32, sm: 44 },
                  lineHeight: 1.15,
                  letterSpacing: "0.16em",
                  color: textBase,
                  mb: 1,
                }}
              >
                {guide.sekki}
              </Typography>
            )}
            <Typography
              sx={{
                fontFamily: MINCHO_FONT,
                fontWeight: 700,
                fontSize: { xs: 18, sm: 22 },
                lineHeight: 1.6,
                color: textBase,
                mb: 2,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {guide.title || "(無題)"}
            </Typography>
            <Box
              sx={{
                "& img": { maxWidth: "100%", height: "auto" },
                "& p": { mt: 0, mb: 1.25 },
                "& h1, & h2, & h3": { mt: 1.75, mb: 1, fontWeight: 800 },
                "& ul, & ol": { pl: 3 },
                "& a": { color: accent },
                "& blockquote": {
                  borderLeft: `4px solid ${accent}55`,
                  pl: 1.5,
                  ml: 0,
                  color: textBase,
                  opacity: 0.85,
                },
                color: textBase,
                fontSize: { xs: 14.5, sm: 15.5 },
                lineHeight: 1.95,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
              dangerouslySetInnerHTML={{ __html: guide.body || "" }}
            />
          </Box>
        ) : (
          // past / future：ヘッダー行クリックで本文をアコーディオン展開
          <Box>
            <Stack
              onClick={hasBody ? () => setExpanded((v) => !v) : undefined}
              direction="row"
              spacing={1.25}
              alignItems="baseline"
              sx={{
                opacity: isPast ? 0.55 : 0.95,
                flexWrap: "wrap",
                cursor: hasBody ? "pointer" : "default",
                borderRadius: 1.5,
                px: hasBody ? 0.75 : 0,
                py: hasBody ? 0.5 : 0,
                mx: hasBody ? -0.75 : 0,
                transition: "background-color 0.15s ease",
                "&:hover": hasBody
                  ? { bgcolor: `${accent}08` }
                  : undefined,
              }}
              useFlexGap
            >
              {guide.sekki && (
                <Typography
                  sx={{
                    fontFamily: MINCHO_FONT,
                    fontSize: { xs: 18, sm: 22 },
                    fontWeight: isPast ? 400 : 600,
                    letterSpacing: "0.12em",
                    color: textBase,
                    lineHeight: 1.2,
                  }}
                >
                  {guide.sekki}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  color: textBase,
                  opacity: 0.55,
                  fontWeight: 700,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {formatGuideRange(guide)}
              </Typography>
              {daysUntil !== null && daysUntil > 0 && (
                <Chip
                  size="small"
                  label={`あと ${daysUntil} 日`}
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 800,
                    bgcolor: `${accent}14`,
                    color: accent,
                    letterSpacing: "0.04em",
                  }}
                />
              )}
              {hasBody && (
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                  <ExpandMoreRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: textBase,
                      opacity: 0.6,
                      transform: expanded ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </Box>
              )}
              <Box sx={{ flexBasis: "100%", height: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  fontFamily: MINCHO_FONT,
                  fontWeight: isPast ? 400 : 600,
                  color: textBase,
                  opacity: isPast ? 0.7 : 0.9,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {guide.title || "(無題)"}
              </Typography>
            </Stack>
            {hasBody && (
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    mt: 1.25,
                    p: { xs: 1.75, sm: 2.25 },
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                    opacity: isPast ? 0.9 : 1,
                    "& img": { maxWidth: "100%", height: "auto" },
                    "& p": { mt: 0, mb: 1 },
                    "& h1, & h2, & h3": { mt: 1.5, mb: 1, fontWeight: 800 },
                    "& ul, & ol": { pl: 3 },
                    "& a": { color: accent },
                    "& blockquote": {
                      borderLeft: `4px solid ${accent}55`,
                      pl: 1.5,
                      ml: 0,
                      color: textBase,
                      opacity: 0.85,
                    },
                    color: textBase,
                    fontSize: { xs: 14, sm: 14.5 },
                    lineHeight: 1.85,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                  dangerouslySetInnerHTML={{ __html: guide.body || "" }}
                />
              </Collapse>
            )}
          </Box>
        )}
      </Box>
    </Stack>
  );
}

function GuideHero({ guide, isUpcoming, accent, textBase, border }) {
  if (!guide) return null;
  const range = formatGuideRange(guide);
  const ds = parseYMD(guide.dateStart || guide.date);
  const daysUntil = ds ? daysFromToday(ds) : null;
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${border}`,
        background: `linear-gradient(160deg, ${accent}14 0%, ${accent}06 50%, rgba(255,255,255,0) 100%)`,
        px: { xs: 2.5, sm: 4.5 },
        py: { xs: 3, sm: 5 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography
          variant="caption"
          sx={{
            letterSpacing: "0.24em",
            fontWeight: 800,
            color: accent,
            textTransform: "uppercase",
            fontSize: 11,
          }}
        >
          {isUpcoming ? "次の節気" : "今の節気"}
        </Typography>
        {isUpcoming && daysUntil !== null && daysUntil > 0 && (
          <Chip
            size="small"
            label={`あと ${daysUntil} 日`}
            sx={{
              bgcolor: accent,
              color: "#fff",
              fontWeight: 800,
              height: 22,
            }}
          />
        )}
      </Stack>

      {guide.sekki && (
        <Typography
          sx={{
            fontFamily: MINCHO_FONT,
            fontWeight: 500,
            fontSize: { xs: 36, sm: 56 },
            lineHeight: 1.1,
            letterSpacing: "0.18em",
            color: textBase,
            mb: 1,
          }}
        >
          {guide.sekki}
        </Typography>
      )}

      {range && (
        <Typography
          variant="caption"
          sx={{
            color: textBase,
            opacity: 0.55,
            fontWeight: 700,
            letterSpacing: "0.06em",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            display: "block",
            mb: 2.5,
          }}
        >
          {range}
        </Typography>
      )}

      <Typography
        sx={{
          fontFamily: MINCHO_FONT,
          fontWeight: 700,
          fontSize: { xs: 20, sm: 24 },
          lineHeight: 1.6,
          color: textBase,
          mb: 2,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {guide.title || "(無題)"}
      </Typography>

      <Box
        sx={{
          "& img": { maxWidth: "100%", height: "auto" },
          "& p": { mt: 0, mb: 1.25 },
          "& h1, & h2, & h3": { mt: 1.75, mb: 1, fontWeight: 800 },
          "& ul, & ol": { pl: 3 },
          "& a": { color: accent },
          "& blockquote": {
            borderLeft: `4px solid ${accent}55`,
            pl: 1.5,
            ml: 0,
            color: textBase,
            opacity: 0.85,
          },
          color: textBase,
          fontSize: { xs: 14.5, sm: 15.5 },
          lineHeight: 1.95,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
        dangerouslySetInnerHTML={{ __html: guide.body || "" }}
      />
    </Box>
  );
}

function GuideNextPreview({ guide, accent, textBase, border, surface }) {
  if (!guide) return null;
  const ds = parseYMD(guide.dateStart || guide.date);
  const daysUntil = ds ? daysFromToday(ds) : null;
  return (
    <Box
      sx={{
        mt: 3,
        borderRadius: 2.5,
        border: `1px dashed ${accent}55`,
        bgcolor: `${accent}05`,
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.75, sm: 2 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.5 }}
        flexWrap="wrap"
        useFlexGap
      >
        <ArrowDownwardRoundedIcon sx={{ color: accent, fontSize: 16 }} />
        <Typography
          variant="caption"
          sx={{
            color: accent,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontSize: 10,
          }}
        >
          次は
        </Typography>
        {daysUntil !== null && daysUntil > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: textBase,
              opacity: 0.7,
              fontWeight: 700,
            }}
          >
            あと {daysUntil} 日
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="baseline"
        sx={{ mb: 0.5 }}
        flexWrap="wrap"
        useFlexGap
      >
        {guide.sekki && (
          <Typography
            sx={{
              fontFamily: MINCHO_FONT,
              fontSize: { xs: 22, sm: 28 },
              fontWeight: 500,
              letterSpacing: "0.14em",
              color: textBase,
            }}
          >
            {guide.sekki}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            color: textBase,
            opacity: 0.55,
            fontWeight: 700,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          {formatGuideRange(guide)}
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        sx={{
          fontFamily: MINCHO_FONT,
          color: textBase,
          opacity: 0.88,
          fontWeight: 600,
          fontSize: { xs: 14.5, sm: 15.5 },
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {guide.title || "(無題)"}
      </Typography>
    </Box>
  );
}

function GuidePastSection({ guides, accent, textBase, border, surface }) {
  const [open, setOpen] = useState(false);
  if (!guides.length) return null;
  return (
    <Box sx={{ mt: 4 }}>
      <Button
        onClick={() => setOpen((v) => !v)}
        endIcon={
          <ExpandMoreRoundedIcon
            sx={{
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.2s ease",
            }}
          />
        }
        sx={{
          textTransform: "none",
          color: textBase,
          fontWeight: 700,
          opacity: 0.75,
        }}
      >
        これまでの節気（{guides.length}件）
      </Button>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          {guides.map((g) => (
            <Box
              key={g.id}
              sx={{
                px: 2,
                py: 1.25,
                borderRadius: 2,
                border: `1px solid ${border}`,
                bgcolor: surface,
                opacity: 0.85,
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 0.5 }}
              >
                {g.sekki && (
                  <Typography
                    sx={{
                      fontFamily: MINCHO_FONT,
                      fontSize: 16,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      color: textBase,
                    }}
                  >
                    {g.sekki}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: textBase,
                    opacity: 0.55,
                    fontWeight: 700,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  }}
                >
                  {formatGuideRange(g)}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: MINCHO_FONT,
                  fontWeight: 600,
                  color: textBase,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {g.title || "(無題)"}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function GuideTab({ guides, loading, accent, textBase, border, surface }) {
  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress size={24} sx={{ color: accent }} />
      </Box>
    );
  }
  if (!guides.length) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography
          sx={{
            fontFamily: MINCHO_FONT,
            color: textBase,
            opacity: 0.55,
            fontSize: 16,
          }}
        >
          まもなくはじまります。
        </Typography>
      </Box>
    );
  }

  const entries = classifyEach(guides);

  return (
    <Box sx={{ py: 1 }}>
      {entries.map((e, idx) => (
        <GuideStepRow
          key={e.guide.id}
          entry={e}
          isLast={idx === entries.length - 1}
          accent={accent}
          textBase={textBase}
          border={border}
          surface={surface}
        />
      ))}
    </Box>
  );
}

/* ──────────────────────────────────────────────────────────
   ルート
   ──────────────────────────────────────────────────────── */
export default function Group2026() {
  const { id } = useParams();
  const navigate = useNavigate();

  const theme = useContext(AppThemeContext) || {};
  const surface = theme.surfaceBg || "#FFFFFF";
  const appBg = theme.appBg || "#F5F5F9";
  const accent = theme.railActive || "#5A35FF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const { userInfo, isAdmin } = useContext(AppUserContext) || {
    userInfo: null,
    isAdmin: false,
  };

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("members");

  const [ddpByUid, setDdpByUid] = useState({});
  const [ddpLoading, setDdpLoading] = useState(false);

  // チャット未読: 最新メッセージと自分の最終既読を比較
  const [chatLatestTs, setChatLatestTs] = useState(0);
  const [chatLastReadTs, setChatLastReadTs] = useState(0);

  const [guides, setGuides] = useState([]);
  const [guidesLoading, setGuidesLoading] = useState(true);

  // 通報の重複防止用：このグループの通報を購読し、reportedUserId → 通報doc の map を保持
  const [reportedMap, setReportedMap] = useState({});

  const isOwnGroup = userInfo?.groupActivity2026?.groupId === id;
  const hasUserInfo = !!userInfo;
  const allowed = hasUserInfo && (isAdmin || isOwnGroup);
  const denied = hasUserInfo && !allowed;

  useEffect(() => {
    let cancelled = false;

    if (!hasUserInfo) {
      setLoading(true);
      setError(null);
      return;
    }
    if (denied) {
      setError("このグループを表示する権限がありません。");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    (async () => {
      try {
        const groupSnap = await getDoc(doc(db, "groupActivity2026Groups", id));
        if (cancelled) return;
        if (!groupSnap.exists()) {
          setError("グループが見つかりませんでした。");
          setLoading(false);
          return;
        }
        setGroup({ id: groupSnap.id, ...groupSnap.data() });
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error("Group2026 fetch error:", err);
        setError("グループ情報の取得に失敗しました。");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, hasUserInfo, denied]);

  // チャット最新メッセージの createdAt を購読（未読バッジ用）
  const currentUid = userInfo?.id || null;
  useEffect(() => {
    if (!id || !currentUid || !allowed) return undefined;
    const qLatest = query(
      collection(db, "groupChats2026", id, "messages"),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    const unsub = onSnapshot(
      qLatest,
      (snap) => {
        const d = snap.docs[0];
        const ts = d?.data()?.createdAt?.toMillis?.() || 0;
        setChatLatestTs(ts);
      },
      (err) => console.error("chat latest subscription error:", err)
    );
    return () => unsub();
  }, [id, currentUid, allowed]);

  // 自分の最終既読 timestamp を購読
  useEffect(() => {
    if (!id || !currentUid || !allowed) return undefined;
    const ref = doc(db, "users", currentUid, "groupChatReads", id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const ts = snap.exists()
          ? snap.data()?.lastReadAt?.toMillis?.() || 0
          : 0;
        setChatLastReadTs(ts);
      },
      (err) => console.error("chat lastRead subscription error:", err)
    );
    return () => unsub();
  }, [id, currentUid, allowed]);

  // チャットタブが開いている間は最終既読を更新（マウント時 + 新着到着時）
  useEffect(() => {
    if (activeTab !== "chat") return;
    if (!id || !currentUid || !allowed) return;
    setDoc(
      doc(db, "users", currentUid, "groupChatReads", id),
      { lastReadAt: serverTimestamp() },
      { merge: true }
    ).catch((err) => console.error("mark chat read error:", err));
  }, [activeTab, id, currentUid, allowed, chatLatestTs]);

  const chatHasUnread =
    chatLatestTs > 0 && chatLatestTs > chatLastReadTs && activeTab !== "chat";

  // ガイドを購読（dateStart 順）
  useEffect(() => {
    setGuidesLoading(true);
    const q = query(collection(db, "guides2026"), orderBy("dateStart", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = [];
        snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        setGuides(arr);
        setGuidesLoading(false);
      },
      (err) => {
        console.error("guides2026 load error:", err);
        setGuidesLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // このグループの通報をリアルタイム購読（重複通報のUIガード用）
  useEffect(() => {
    if (!id) return undefined;
    const q = query(
      collection(db, "groupReports2026"),
      where("groupId", "==", id)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const map = {};
        snap.forEach((d) => {
          const r = d.data();
          if (r?.reportedUserId) map[r.reportedUserId] = { id: d.id, ...r };
        });
        setReportedMap(map);
      },
      (err) => {
        console.error("groupReports2026 subscribe error:", err);
      }
    );
    return () => unsub();
  }, [id]);

  const members = useMemo(
    () => (Array.isArray(group?.members) ? group.members : []),
    [group]
  );

  const [liveProfileByUid, setLiveProfileByUid] = useState({});

  // メンバーごとに DDP と最新プロフィール（name / avatar / 住所系）を並列取得
  //   DDP: users/{uid}/ddps の最初の1件
  //   profile: users/{uid}（/my-page での変更を仲間タブに反映するため都度フェッチ）
  useEffect(() => {
    if (members.length === 0) {
      setDdpByUid({});
      setLiveProfileByUid({});
      return;
    }
    let cancelled = false;
    setDdpLoading(true);
    (async () => {
      try {
        const results = await Promise.all(
          members.map(async (m) => {
            const [ddp, profile] = await Promise.all([
              (async () => {
                try {
                  const q = query(
                    collection(db, "users", m.uid, "ddps"),
                    limit(1)
                  );
                  const snap = await getDocs(q);
                  return snap.docs[0]?.data()?.ddp || "";
                } catch (e) {
                  console.warn("ddp load failed for", m.uid, e);
                  return "";
                }
              })(),
              (async () => {
                try {
                  const userSnap = await getDoc(doc(db, "users", m.uid));
                  if (!userSnap.exists()) return null;
                  const d = userSnap.data();
                  const pick = (key) =>
                    typeof d[key] === "string" ? d[key] : null;
                  return {
                    name: d.name || null,
                    avatar: pick("avatar"),
                    prefecture: pick("prefecture"),
                    city: pick("city"),
                    usState: pick("usState"),
                    country: pick("country"),
                  };
                } catch (e) {
                  console.warn("user profile load failed for", m.uid, e);
                  return null;
                }
              })(),
            ]);
            return [m.uid, ddp, profile];
          })
        );
        if (cancelled) return;
        const ddpMap = {};
        const profileMap = {};
        results.forEach(([uid, ddp, profile]) => {
          ddpMap[uid] = ddp;
          if (profile) profileMap[uid] = profile;
        });
        setDdpByUid(ddpMap);
        setLiveProfileByUid(profileMap);
      } finally {
        if (!cancelled) setDdpLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [members]);

  const liveMembers = useMemo(
    () =>
      members.map((m) => {
        const live = liveProfileByUid[m.uid];
        if (!live) return m;
        const next = { ...m };
        if (live.name) next.name = live.name;
        // 住所系: users/{uid} で string として存在する場合のみ上書き
        //   （null/undefined = 取得失敗もしくは未設定なので、denormalized 値を温存）
        ["avatar", "prefecture", "city", "usState", "country"].forEach((k) => {
          if (live[k] !== null && live[k] !== undefined) next[k] = live[k];
        });
        return next;
      }),
    [members, liveProfileByUid]
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: appBg,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" sx={{ color: textBase, opacity: 0.7 }}>
            グループ情報を読み込んでいます…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: appBg,
          gap: 2,
          px: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: textBase, textAlign: "center" }}>
          {error}
        </Typography>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          戻る
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        pb: { xs: 7, md: 4 },
        background: appBg,
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 3 },
          pb: { xs: 3, sm: 4 },
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: "100%", md: 1100 },
            width: "100%",
            mx: "auto",
            borderRadius: { xs: 3, sm: 4 },
            bgcolor: surface,
            boxShadow: "0 20px 60px rgba(20,16,40,0.10)",
            border: `1px solid ${border}`,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* ヘッダー */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              borderBottom: `1px solid ${border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: `${accent}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GroupsRoundedIcon sx={{ color: accent, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    fontSize: 11,
                    opacity: 0.7,
                    color: textBase,
                  }}
                >
                  MASTER MIND GROUP 2026
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "0.03em",
                    color: textBase,
                  }}
                >
                  {group?.name || "（無題のグループ）"}
                </Typography>
              </Box>
            </Stack>

          </Box>

          {/* タブ */}
          <Box
            sx={{
              borderBottom: `1px solid ${border}`,
              bgcolor: surface,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{ style: { backgroundColor: accent, height: 3 } }}
              sx={{
                minHeight: 48,
                maxWidth: "100%",
                "& .MuiTab-root": {
                  minHeight: 48,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  color: textBase,
                  opacity: 0.65,
                  textTransform: "none",
                  minWidth: { xs: "auto", sm: 100 },
                  px: { xs: 1.25, sm: 2 },
                  fontSize: { xs: 13, sm: 14 },
                },
                "& .Mui-selected": { color: `${accent} !important`, opacity: 1 },
              }}
            >
              <Tab
                value="members"
                icon={<PeopleRoundedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="仲間"
              />
              <Tab
                value="guide"
                icon={<MenuBookRoundedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="ガイド"
              />
              <Tab
                value="chat"
                icon={
                  <Badge
                    variant="dot"
                    color="error"
                    invisible={!chatHasUnread}
                    overlap="circular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />
                  </Badge>
                }
                iconPosition="start"
                label="チャット"
              />
            </Tabs>
          </Box>

          {/* 本文 */}
          <Box sx={{ px: { xs: 2.5, sm: 3 }, py: { xs: 2, sm: 3.5 } }}>
            {activeTab === "members" && (
              <MembersTab
                members={liveMembers}
                ddpByUid={ddpByUid}
                ddpLoading={ddpLoading}
                accent={accent}
                textBase={textBase}
                border={border}
                surface={surface}
                groupId={id}
                currentUid={userInfo?.id}
                currentUserName={userInfo?.name}
                reportedMap={reportedMap}
              />
            )}
            {activeTab === "guide" && (
              <GuideTab
                guides={guides}
                loading={guidesLoading}
                accent={accent}
                textBase={textBase}
                border={border}
                surface={surface}
              />
            )}
            {activeTab === "chat" && (
              <ChatTab2026
                groupId={id}
                members={liveMembers}
                accent={accent}
                textBase={textBase}
                border={border}
                surface={surface}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
